
import { defineComponent, ref, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import {
  listFeedbacks,
  replyFeedback,
  updateFeedbackStatus,
  Feedback,
  FeedbackStatus,
  PaginatedFeedbacks,
  formatFeedbackStatus,
  formatFeedbackType,
} from '@/services/admin'
import DataTable from './DataTable.vue'

export default defineComponent({
  name: 'FeedbackManagement',
  components: { DataTable },
  setup() {
    const feedbacks = ref<Feedback[]>([])
    const loading = ref(false)
    const page = ref(1)
    const pageSize = ref(20)
    const total = ref(0)

    const feedbackDetailVisible = ref(false)
    const selectedFeedback = ref<Feedback | null>(null)
    const replyContent = ref('')
    const replySaving = ref(false)
    const selectedStatus = ref<FeedbackStatus>('pending')
    const statusUpdating = ref(false)

    const priorityOrder: Record<string, number> = { bug: 0, feature: 1, question: 2, other: 3 }

    const sortedFeedbacks = computed(() => {
      return [...feedbacks.value].sort((a, b) => {
        const pa = priorityOrder[a.type] ?? 99
        const pb = priorityOrder[b.type] ?? 99
        if (pa !== pb) return pa - pb
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
    })

    function formatResponseTime(feedback: Feedback): string {
      if (!feedback.first_response_at) return '未回复'
      const created = new Date(feedback.created_at).getTime()
      const responded = new Date(feedback.first_response_at).getTime()
      const diffMs = responded - created
      const diffHours = Math.round(diffMs / (1000 * 60 * 60) * 10) / 10
      if (diffHours < 1) return '<1小时'
      if (diffHours < 24) return `${diffHours}小时`
      return `${Math.round(diffHours / 24 * 10) / 10}天`
    }

    const columns = [
      { title: '用户', key: 'user' },
      { title: '类型', key: 'type' },
      { title: '标题', dataIndex: 'title', key: 'title' },
      { title: '状态', key: 'status' },
      { title: '响应时间', key: 'responseTime' },
      { title: '创建时间', dataIndex: 'created_at', key: 'created_at' },
      { title: '操作', key: 'action' },
    ]

    const loadFeedbacks = async () => {
      loading.value = true
      try {
        const result: PaginatedFeedbacks = await listFeedbacks(page.value, pageSize.value)
        feedbacks.value = result.list
        total.value = result.total
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.error('load feedbacks failed', error)
        message.error('加载反馈数据失败')
      }
      finally {
        loading.value = false
      }
    }

    const handleTableChange = (newPage: number, newPageSize: number) => {
      page.value = newPage
      pageSize.value = newPageSize
      loadFeedbacks()
    }

    const openFeedbackDetail = (feedback: Feedback) => {
      selectedFeedback.value = feedback
      selectedStatus.value = feedback.status
      replyContent.value = ''
      feedbackDetailVisible.value = true
    }

    const closeFeedbackDetail = () => {
      feedbackDetailVisible.value = false
      selectedFeedback.value = null
      replyContent.value = ''
    }

    const submitReply = async () => {
      if (!selectedFeedback.value) return
      const content = replyContent.value.trim()
      if (!content) {
        message.error('请输入回复内容')
        return
      }
      replySaving.value = true
      try {
        await replyFeedback(selectedFeedback.value.id, content)
        replyContent.value = ''
        await loadFeedbacks()
        const updated = feedbacks.value.find((item) => item.id === selectedFeedback.value?.id)
        if (updated) {
          selectedFeedback.value = updated
        }
      }
      catch (error) {
        message.error(error instanceof Error ? error.message : '回复失败')
      }
      finally {
        replySaving.value = false
      }
    }

    const changeFeedbackStatus = async () => {
      if (!selectedFeedback.value) return
      statusUpdating.value = true
      try {
        await updateFeedbackStatus(selectedFeedback.value.id, selectedStatus.value)
        await loadFeedbacks()
        const updated = feedbacks.value.find((item) => item.id === selectedFeedback.value?.id)
        if (updated) {
          selectedFeedback.value = updated
        }
      }
      catch (error) {
        message.error(error instanceof Error ? error.message : '状态更新失败')
      }
      finally {
        statusUpdating.value = false
      }
    }

    onMounted(() => {
      loadFeedbacks()
    })

    return {
      feedbacks,
      sortedFeedbacks,
      formatResponseTime,
      loading,
      page,
      pageSize,
      total,
      columns,
      handleTableChange,
      feedbackDetailVisible,
      selectedFeedback,
      replyContent,
      replySaving,
      selectedStatus,
      statusUpdating,
      formatFeedbackStatus,
      formatFeedbackType,
      openFeedbackDetail,
      closeFeedbackDetail,
      submitReply,
      changeFeedbackStatus,
    }
  },
})
